import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * MCP connectors (al-tutor migration 182, PRD agent-mcp-connectors).
 *
 * The credential is write-only: it goes out in create/update and never comes
 * back — `has_auth_secret` says whether one is stored.
 */

export type McpTransport = 'http' | 'sse';
export type McpAuthType = 'none' | 'bearer' | 'header';
export type McpNotice = 'fees' | 'content' | 'instructions';

export interface McpTool {
  id: string;
  tool_name: string;
  /** what the agent sees: <slug>__<tool> */
  agent_tool_name: string;
  description_effective?: string | null;
  description_server?: string | null;
  description_original?: string | null;
  input_schema: Record<string, any>;
  is_enabled: boolean;
  description_changed: boolean;
  schema_changed: boolean;
  removed: boolean;
  last_seen_at?: string | null;
}

export interface McpConnector {
  id: string;
  name: string;
  slug: string;
  server_url: string;
  transport: McpTransport;
  auth_type: McpAuthType;
  auth_header_name?: string | null;
  has_auth_secret: boolean;
  is_active: boolean;
  acknowledged_by?: string | null;
  acknowledged_at?: string | null;
  acknowledgement_current: boolean;
  timeout_seconds: number;
  max_calls_per_turn: number;
  max_result_chars: number;
  consecutive_failures: number;
  disabled_reason?: string | null;
  disabled_at?: string | null;
  server_name?: string | null;
  server_version?: string | null;
  protocol_version?: string | null;
  last_discovered_at?: string | null;
  tools: McpTool[];
  assigned_bot_ids: string[];
  calls_30d: number;
  failed_calls_30d: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface McpConnectorInput {
  name?: string;
  slug?: string | null;
  server_url?: string;
  transport?: McpTransport;
  auth_type?: McpAuthType;
  auth_header_name?: string | null;
  /** write-only; omit to keep the stored one */
  auth_secret?: string | null;
  clear_auth_secret?: boolean;
  timeout_seconds?: number;
  max_calls_per_turn?: number;
  max_result_chars?: number;
}

export interface McpToolInput {
  description_effective?: string;
  is_enabled?: boolean;
  accept_server_version?: boolean;
}

export interface McpDryRunResult {
  ok: boolean;
  is_error: boolean;
  error_kind?: string | null;
  message?: string | null;
  result?: string | null;
  duration_ms: number;
}

/** Keep in sync with al-tutor MCP_MAX_TOOLS_PER_AGENT. */
export const MCP_MAX_TOOLS_PER_AGENT = 12;
/** Keep in sync with al-tutor-lgs mcp_tools.AUTO_DISABLE_AFTER_FAILURES. */
export const MCP_AUTO_DISABLE_AFTER_FAILURES = 5;

/** A server from the official MCP Registry, ready for the connector dialog. */
export interface McpRegistryEntry {
  registry_name: string;
  title: string;
  description: string;
  url: string;
  transport: 'http' | 'sse';
  domain: string | null;
  /** published under the domain owner's own namespace (com.notion on mcp.notion.com) */
  official: boolean;
  website?: string | null;
  auth: {
    /** false = nothing declared: no sign-in, or OAuth (not supported) */
    known: boolean;
    preset: 'none' | 'bearer' | 'x-api-key' | 'api-key' | 'authorization' | 'custom';
    header_name: string | null;
    /** the provider's own words on where to get the key */
    hint: string | null;
    required: boolean;
  };
  /** fit to the query (backend ranking); higher first */
  relevance?: number;
}

@Injectable({ providedIn: 'root' })
export class McpService {
  private readonly base = `${environment.tutorsBackend}/mcp-connectors`;

  constructor(private http: HttpClient) {}

  list(): Observable<McpConnector[]> {
    return this.http.get<{ data: McpConnector[] }>(this.base).pipe(map((r) => r.data));
  }

  get(id: string): Observable<McpConnector | undefined> {
    return this.list().pipe(map((rows) => rows.find((c) => c.id === id)));
  }

  create(input: McpConnectorInput): Observable<McpConnector> {
    return this.http.post<McpConnector>(this.base, input);
  }

  update(id: string, input: McpConnectorInput): Observable<McpConnector> {
    return this.http.put<McpConnector>(`${this.base}/${id}`, input);
  }

  delete(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/${id}`);
  }

  discover(id: string): Observable<McpConnector> {
    return this.http.post<McpConnector>(`${this.base}/${id}/discover`, {});
  }

  updateTool(id: string, toolId: string, input: McpToolInput): Observable<McpConnector> {
    return this.http.put<McpConnector>(`${this.base}/${id}/tools/${toolId}`, input);
  }

  /** Live search in the official MCP Registry (remote servers only). The
   *  dialog shows failures itself — no global toast. */
  searchRegistry(q: string, cursor?: string | null): Observable<{ data: McpRegistryEntry[]; next_cursor: string | null }> {
    const params: Record<string, string> = { q };
    if (cursor) params['cursor'] = cursor;
    return this.http.get<{ data: McpRegistryEntry[]; next_cursor: string | null }>(`${this.base}/registry`, {
      params,
      headers: new HttpHeaders({ 'X-Suppress-Error-Toast': '1' }),
    });
  }

  /** Enable / disable every tool the server offers (or only `toolIds`). */
  setAllTools(id: string, enabled: boolean, toolIds?: string[]): Observable<McpConnector> {
    return this.http.put<McpConnector>(`${this.base}/${id}/tools`, {
      is_enabled: enabled,
      tool_ids: toolIds ?? null,
    });
  }

  acknowledge(id: string, notices: McpNotice[]): Observable<McpConnector> {
    return this.http.post<McpConnector>(`${this.base}/${id}/acknowledge`, { notices });
  }

  activate(id: string): Observable<McpConnector> {
    return this.http.post<McpConnector>(`${this.base}/${id}/activate`, {});
  }

  deactivate(id: string): Observable<McpConnector> {
    return this.http.post<McpConnector>(`${this.base}/${id}/deactivate`, {});
  }

  dryRun(id: string, toolName: string, args: Record<string, any>): Observable<McpDryRunResult> {
    return this.http.post<McpDryRunResult>(`${this.base}/${id}/dry-run`, {
      tool_name: toolName,
      arguments: args,
    });
  }

  // ── Agent side ──

  listForBot(tutorId: string): Observable<McpConnector[]> {
    return this.http
      .get<{ data: McpConnector[] }>(`${environment.tutorsBackend}/tutor/${tutorId}/mcp-connectors`)
      .pipe(map((r) => r.data));
  }

  assignToBot(tutorId: string, connectorIds: string[]): Observable<McpConnector[]> {
    return this.http
      .put<{ data: McpConnector[] }>(`${environment.tutorsBackend}/tutor/${tutorId}/mcp-connectors`, {
        connector_ids: connectorIds,
      })
      .pipe(map((r) => r.data));
  }
}
